import { toast } from "../Toast";
import { AGENT_NAME_KEY, COMPANY_NAME_KEY, LOGO_KEY } from "../config/constants";
import { track } from "./analytics";

// Escapes a user-supplied string for safe interpolation into the export HTML.
const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const readLS = (key) => {
  try { return localStorage.getItem(key) || ""; } catch { return ""; }
};

const formatToday = () => {
  try {
    return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
};

const openPrintDoc = (html) => {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank");
  if (!w) {
    toast.error("Pop-up blocked. Enable pop-ups to export.");
    URL.revokeObjectURL(url);
    return false;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Generic single-flow text exporter used by every tool EXCEPT the CMA report. Branded
// letterhead: logo/company header, title block, pre-wrap body, per-page Pitcherly footer.
export function printableExport(title, text, opts = {}) {
  try {
    const address = opts.address || "";
    const logo = readLS(LOGO_KEY);
    const company = readLS(COMPANY_NAME_KEY);
    const agent = readLS(AGENT_NAME_KEY);

    const safeTitle = esc(title);
    const safeText = esc(text);
    const safeAddr = esc(address);
    const today = esc(formatToday());

    const hasLogo = !!logo;
    const idHtml =
      (company || agent)
        ? `<div class="brand-id">${company ? `<div class="brand-company">${esc(company)}</div>` : ""}${agent ? `<div class="brand-agent">${esc(agent)}</div>` : ""}</div>`
        : "";
    const headerInner = hasLogo
      ? `<img class="brand-logo" src="${esc(logo)}" alt="" />${idHtml}`
      : (idHtml || `<div class="brand-id"><div class="brand-company">Pitcher<span style="color:#1E40AF">ly</span></div></div>`);
    const headerClass = hasLogo ? "brand-header" : "brand-header center";

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
      <style>
        @page { margin: 2cm; }
        * { box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; color: #1a1a1a; margin: 0; padding: 0 0 64px; font-size: 12pt; line-height: 1.5; }
        .doc { max-width: 720px; margin: 0 auto; padding: 0 6px; }
        .brand-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 14px; border-bottom: 2px solid #1E40AF; margin-bottom: 22px; }
        .brand-header.center { justify-content: center; text-align: center; }
        .brand-logo { max-height: 60px; max-width: 260px; object-fit: contain; }
        .brand-id { text-align: right; }
        .brand-header.center .brand-id { text-align: center; }
        .brand-company { font-size: 14pt; font-weight: 700; color: #111; font-family: Georgia, serif; }
        .brand-agent { font-size: 10.5pt; color: #555; margin-top: 2px; font-family: Arial, sans-serif; }
        .title-block { margin-bottom: 22px; break-inside: avoid; }
        .doc-title { font-size: 19pt; font-weight: 700; color: #111; margin: 0 0 5px; }
        .doc-addr { font-size: 11.5pt; color: #444; margin: 0 0 3px; }
        .doc-date { font-size: 10pt; color: #777; font-family: Arial, sans-serif; }
        .body { white-space: pre-wrap; font-size: 11.5pt; line-height: 1.6; }
        .pitcherly-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 8.5pt; color: #9a9a9a; padding: 7px 0; font-family: Arial, sans-serif; }
      </style></head><body>
      <div class="doc">
        <div class="${headerClass}">${headerInner}</div>
        <div class="title-block">
          <h1 class="doc-title">${safeTitle}</h1>
          ${address ? `<div class="doc-addr">${safeAddr}</div>` : ""}
          <div class="doc-date">${today}</div>
        </div>
        <div class="body">${safeText}</div>
      </div>
      <div class="pitcherly-footer">Powered by Pitcherly — pitcherly.com</div>
      <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
      </body></html>`;

    if (openPrintDoc(html)) track("pdf_exported", { tool: title });
  } catch (e) {
    toast.error("Could not open print window.");
  }
}

// ─── Structured CMA report — dense 4-page portrait (US Letter) document ───────
// Dedicated exporter for the CMA tool (other tools keep printableExport above). Print CSS
// only, Blob-URL + window.print, no PDF/chart library. Takes structured DATA: cover, an
// overview+valuation+analysis page, a pricing-position + comps page, and a detailed-comparison
// page where the SUBJECT is the reference banner and comps are color-coded rows. Google imagery
// is passed in as data URLs; any missing image degrades to a tasteful placeholder. A manual CMA
// (no comps) renders just the cover + overview.

const toNum = (v) => {
  if (v == null) return null;
  // Number("") is 0 — guard the empty/sign-only cases so a blank field reads as missing
  // (no fabricated $0 notes or "0" tiles), not a real zero.
  const cleaned = String(v).replace(/[^0-9.-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};
const usd = (v) => {
  const n = toNum(v);
  return n != null && n !== 0 ? `$${Math.round(n).toLocaleString()}` : "";
};
const intStr = (v) => {
  const n = toNum(v);
  return n != null ? Math.round(n).toLocaleString() : "";
};
// recommended_price/offer can arrive as a bare number ("920000"), a bare numeric range
// ("900000-906000"), or an already-formatted string with reasoning ("$915,000 — ~1% below
// list"). Format bare numbers/ranges as full USD; leave anything that already carries $/commas
// or prose UNTOUCHED so the model's wording (and seller's already-correct output) is never
// mangled. Defensive: the prompt also asks the model for full-dollar figures, this is a backstop.
const fmtRec = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  const one = (tok) => {
    const d = tok.replace(/[^0-9.]/g, "");
    const n = Number(d);
    return d && d !== "." && Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : tok;
  };
  if (/^\$?\d[\d,]*(\.\d+)?$/.test(s)) return one(s);
  const m = s.match(/^\$?\s*(\d[\d,]*(?:\.\d+)?)\s*[-–—]\s*\$?\s*(\d[\d,]*(?:\.\d+)?)\s*$/);
  if (m) return `${one(m[1])}–${one(m[2])}`;
  // Leading bare full-dollar figure (4+ digits, not already comma-grouped) followed by prose,
  // e.g. "920000 — ~1% below list" or "920000 (offer below list)": format that figure, leave the
  // prose. Already-grouped strings ("$915,000 — …") have ≤3 leading digits before a comma, so the
  // \d{4,} anchor never matches them — compliant model output is left untouched.
  return s.replace(/^(\$?)(\d{4,})(?![\d,])/, (_m, _d, num) => `$${Number(num).toLocaleString()}`);
};

// payload: { mode, branding{logo,company}, agent{name,signoff,headshot},
//   property{address,beds,baths,sqft,lot,year,type,lat,lng,photo}, mapImage,
//   valuation{estimate,rangeLow,rangeHigh,recommendedPrice,valueRange,askingPrice},
//   comps[{address,price,bedrooms,bathrooms,sqft,lotSize,yearBuilt,distance,daysOld,listingType,photo}],
//   narrative }
export function exportCmaReport(payload = {}) {
  try {
    const P = payload || {};
    const branding = P.branding || {}, agent = P.agent || {}, property = P.property || {}, valuation = P.valuation || {};
    const comps = Array.isArray(P.comps) ? P.comps : [];
    const isBuyer = P.mode === "buyer";
    const noComps = comps.length === 0;

    const logo = branding.logo || "", company = branding.company || "";
    const agentName = agent.name || "", headshot = agent.headshot || "";
    const address = property.address || "", heroPhoto = property.photo || "", mapImage = P.mapImage || "";
    const today = formatToday();
    const docTitle = isBuyer ? "Comparative Market Analysis — Buyer's Offer Guidance" : "Comparative Market Analysis";

    // Street line vs locality (city/state/zip after the first comma).
    const firstComma = address.indexOf(",");
    const streetLine = firstComma > 0 ? address.slice(0, firstComma).trim() : address;
    const locality = firstComma > 0 ? address.slice(firstComma + 1).trim() : "";

    // Subject reference values — the yardstick the comps are measured against.
    const estimate = toNum(valuation.estimate), low = toNum(valuation.rangeLow), high = toNum(valuation.rangeHigh);
    const hasAvm = estimate != null; // a market-data CMA ran (vs a trial/lite manual CMA)
    const asking = toNum(valuation.askingPrice);
    const subjSqft = toNum(property.sqft), subjBeds = toNum(property.beds), subjBaths = toNum(property.baths);
    const subjLot = toNum(property.lot), subjYear = toNum(property.year);
    const subjPrice = asking != null ? asking : estimate;
    const subjPpsf = subjPrice != null && subjSqft ? subjPrice / subjSqft : null;
    const hasBar = estimate != null && low != null && high != null && high > low;
    const recLabel = isBuyer ? "Recommended offer" : "Recommended list price";

    const ph = (src, cls, label) => src
      ? `<img class="${cls}" src="${esc(src)}" alt="" />`
      : `<div class="${cls} ph"><span>${esc(label || "Photo unavailable")}</span></div>`;

    const footer = (n, total) => `<div class="pg-footer">
        <span>Prepared by ${esc(agentName) || "Your Agent"}${company ? `, ${esc(company)}` : ""} &middot; ${esc(today)}</span>
        <span>${n} of ${total}</span>
        <span>Powered by Pitcherly &mdash; pitcherly.com</span>
      </div>`;
    const header = `<div class="pg-header">
        <span class="ph-addr">${esc(streetLine) || "Comparative Market Analysis"}</span>
        <span class="ph-mid">${esc(docTitle)}</span>
        <span class="ph-right">${logo ? `<img class="ph-logo" src="${esc(logo)}" alt="" />` : esc(company || "Pitcherly")}</span>
      </div>`;

    // ── PAGE 1: Cover ──
    const coverBrand = logo
      ? `<img class="cv-logo" src="${esc(logo)}" alt="" />`
      : `<div class="cv-wordmark">${company ? esc(company) : 'Pitcher<span>ly</span>'}</div>`;
    const coverInner = `
      <div class="cv-top">${coverBrand}<div class="cv-kicker">${isBuyer ? "Comparative Market Analysis &middot; Buyer's Offer Guidance" : "Comparative Market Analysis"}</div></div>
      <div class="cv-hero">${ph(heroPhoto, "cv-photo", "Property photo")}</div>
      <div class="cv-addr-block">
        <div class="cv-street">${esc(streetLine) || "Subject Property"}</div>
        ${locality ? `<div class="cv-locality">${esc(locality)}</div>` : ""}
      </div>
      <div class="cv-prep">
        <div class="cv-prep-divider"></div>
        <div class="cv-prep-row">
          ${headshot ? `<img class="cv-headshot" src="${esc(headshot)}" alt="" />` : ""}
          <div class="cv-prep-txt">
            <div class="cv-prep-label">Prepared by</div>
            <div class="cv-prep-name">${esc(agentName) || "Your Agent"}</div>
            ${company ? `<div class="cv-prep-co">${esc(company)}</div>` : ""}
            <div class="cv-prep-date">${esc(today)}</div>
          </div>
        </div>
      </div>`;

    // ── PAGE 2: Overview & valuation ──
    let askingNote = "";
    if (hasBar && asking != null) {
      const mid = (low + high) / 2;
      const where = asking < low ? "below" : asking > high ? "above"
        : (Math.abs(asking - mid) <= (high - low) * 0.12 ? "near the midpoint of" : "within");
      askingNote = `The ${usd(asking)} ${isBuyer ? "list" : "asking"} price sits ${where} the estimated value range.`;
    }
    const valPanel = `<div class="val-card">
        <div class="val-label">Estimated Market Value</div>
        <div class="val-figure">${hasBar ? usd(estimate) : esc(valuation.valueRange || "—")}</div>
        ${hasBar ? `<div class="val-range">Range ${usd(low)} &ndash; ${usd(high)}</div>` : ""}
        ${valuation.recommendedPrice ? `<div class="val-rec">${recLabel}: ${esc(fmtRec(valuation.recommendedPrice))}</div>` : ""}
        ${askingNote ? `<div class="val-note">${esc(askingNote)}</div>` : ""}
      </div>`;
    const specDefs = [
      ["Beds", subjBeds != null ? String(subjBeds) : ""],
      ["Baths", subjBaths != null ? String(subjBaths) : ""],
      ["Square Feet", intStr(property.sqft)],
      ["$ / Sq Ft", subjPpsf != null ? usd(subjPpsf) : ""],
      ["Lot Size", subjLot != null ? `${intStr(property.lot)} sqft` : ""],
      ["Year Built", subjYear != null ? String(subjYear) : ""],
      ["Property Type", property.type ? String(property.type) : ""],
    ];
    // Market-data CMA: keep every row, rendering "—" for fields RentCast didn't return (e.g. an
    // address with no house number → no subject record), so Property Details stays consistent
    // with the comparison banner instead of silently dropping Lot/Year/Type. Manual CMA: those
    // fields are never expected, so only show what was provided (no wall of dashes).
    const specs = hasAvm
      ? specDefs.map(([k, v]) => [k, v !== "" && v != null ? v : "—"])
      : specDefs.filter(([, v]) => v !== "" && v != null);
    // Subtle note ONLY when RentCast returned no subject record at all (e.g. an address with no
    // house number) — the genuine "enter a full address" case. A matched-but-sparse record
    // (condos have no lot; county data often omits year/type) shows "—" rows without the note,
    // since adding a house number wouldn't recover data the provider simply didn't return. Older
    // saved CMAs lack the flag (undefined) → note doesn't fire (graceful).
    const subjIncomplete = hasAvm && property.subjectMatched === false;
    const specGrid = specs.length
      ? `<div class="spec-grid">${specs.map(([k, v]) => `<div class="spec"><div class="spec-k">${esc(k)}</div><div class="spec-v">${esc(String(v))}</div></div>`).join("")}</div>${subjIncomplete ? `<p class="muted note">Some subject details weren't found for this address. Enter a full street address (including house number) for complete property details.</p>` : ""}`
      : `<p class="muted">Property details unavailable.</p>`;
    const paras = String(P.narrative || "").split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
    const narrativeHtml = paras.length
      ? paras.map(p => `<p class="narr">${esc(p)}</p>`).join("")
      : `<p class="narr muted">No written analysis was generated.</p>`;
    const overviewInner = `${header}
      <div class="pg-body">
        <div class="ov-top">
          <div class="ov-imgs">
            ${ph(heroPhoto, "ov-photo", "Property photo")}
            ${mapImage ? `<img class="ov-map" src="${esc(mapImage)}" alt="" />` : (hasAvm ? `<div class="ov-map ph"><span>Map unavailable</span></div>` : "")}
          </div>
          ${valPanel}
        </div>
        <h2 class="sec">Property Details</h2>
        ${specGrid}
        <h2 class="sec">Market Analysis</h2>
        ${narrativeHtml}
        ${noComps ? (hasAvm
          ? `<p class="muted note">No comparable sales were available for this address; the estimate above reflects RentCast's automated valuation.</p>`
          : `<p class="muted note">Market-data comparables and a live value estimate are available on Starter and Pro plans.</p>`) : ""}
      </div>`;

    // ── PAGE 3: Pricing position + comparable sales ──
    const compPrices = comps.map(c => toNum(c.price)).filter(n => n != null).sort((a, b) => a - b);
    let rangeBar = "";
    if (compPrices.length) {
      // Scale: prefer the AVM confidence range so the Estimate/Asking markers sit at their true
      // proportional position within the band — the estimate lands at the midpoint only when the
      // band is symmetric (as RentCast's typically is, matching the page-2 "midpoint of the range"
      // framing) — instead of being skewed off-axis by a single outlier comp. With no AVM band,
      // derive an outlier-resistant scale from the comps (drop prices beyond 1.5×IQR) so one weird
      // parcel (e.g. a 1.7-acre, $930/sqft comp) doesn't blow out the range. Either way the actual
      // comp sale prices are plotted as dots; any that fall outside the band clamp to the end with
      // a ‹/› caret instead of stretching the scale.
      let scaleLow, scaleHigh;
      if (hasBar) {
        scaleLow = low; scaleHigh = high;
      } else {
        const q = (arr, p) => { const i = (arr.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i); return arr[lo] + (arr[hi] - arr[lo]) * (i - lo); };
        const q1 = q(compPrices, 0.25), q3 = q(compPrices, 0.75), iqr = q3 - q1;
        const inliers = compPrices.filter(p => p >= q1 - 1.5 * iqr && p <= q3 + 1.5 * iqr);
        const usable = inliers.length ? inliers : compPrices;
        scaleLow = usable[0]; scaleHigh = usable[usable.length - 1];
      }
      const span = scaleHigh - scaleLow;
      const pos = v => (span > 0 ? Math.max(0, Math.min(100, ((v - scaleLow) / span) * 100)) : 50);
      const anyOOR = compPrices.some(p => p < scaleLow || p > scaleHigh);
      const compDots = compPrices.map(p => {
        const oor = p < scaleLow ? "‹" : p > scaleHigh ? "›" : "";
        return `<div class="rb-dot${oor ? " oor" : ""}" style="left:${pos(p)}%">${oor}</div>`;
      }).join("");
      const estM = estimate != null ? pos(estimate) : null, askM = asking != null ? pos(asking) : null;
      rangeBar = `<div class="rb">
          <div class="rb-track"></div>
          ${compDots}
          ${estM != null ? `<div class="rb-mark est" style="left:${estM}%"><span>Estimate ${usd(estimate)}</span></div>` : ""}
          ${askM != null ? `<div class="rb-mark ask" style="left:${askM}%"><span>${isBuyer ? "List" : "Asking"} ${usd(asking)}</span></div>` : ""}
        </div>
        <div class="rb-ticks"><span>Low ${usd(scaleLow)}</span><span>High ${usd(scaleHigh)}</span></div>
        <p class="rb-cap">Band = ${hasBar ? "estimated value range" : "comparable sale range"}; dots = comparable sale prices${anyOOR ? " (‹ › marks sales outside the range)" : ""}.</p>`;
    }
    const statusBadge = c => {
      const t = String(c.listingType || c.status || "").toLowerCase();
      if (t.includes("sold")) return `<span class="badge b-sold">Sold</span>`;
      if (t.includes("pending")) return `<span class="badge b-pend">Pending</span>`;
      if (t.includes("active") || t.includes("sale")) return `<span class="badge b-act">Active</span>`;
      return `<span class="badge b-comp">Comp</span>`;
    };
    const compsRows = comps.map((c, i) => {
      const price = toNum(c.price), sq = toNum(c.sqft);
      return `<tr>
        <td class="num">${i + 1}</td>
        <td>${ph(c.photo, "cmp-thumb", "—")}</td>
        <td class="addr">${esc((c.address || "—").split(",")[0])}<div class="addr-badge">${statusBadge(c)}</div></td>
        <td>${esc(String(c.bedrooms ?? "—"))}/${esc(String(c.bathrooms ?? "—"))}</td>
        <td class="num">${sq ? intStr(sq) : "—"}</td>
        <td class="num">${price != null && sq ? usd(price / sq) : "—"}</td>
        <td class="num">${price != null ? usd(price) : "—"}</td>
        <td class="num">${c.distance != null ? `${esc(String(c.distance))} mi` : "—"}</td>
      </tr>`;
    }).join("");
    const pricingInner = `${header}
      <div class="pg-body">
        <h2 class="sec">Pricing Position</h2>
        ${rangeBar || `<p class="muted">Comparable price data unavailable.</p>`}
        <h2 class="sec">Comparable Sales</h2>
        <table class="tbl">
          <thead><tr><th>#</th><th>Photo</th><th>Address</th><th>Bd/Ba</th><th class="num">SqFt</th><th class="num">$/SqFt</th><th class="num">Sale Price</th><th class="num">Dist</th></tr></thead>
          <tbody>${compsRows}</tbody>
        </table>
        <p class="foot-note">Comparable sales and value estimate via RentCast; street-level imagery via Google Street View. ${esc(today)}.</p>
      </div>`;

    // ── PAGE 4: Detailed comparison — subject as reference banner, comps as rows ──
    const bannerStat = (k, v) => `<div class="sb-stat"><span class="sb-k">${esc(k)}</span><span class="sb-v">${esc(v)}</span></div>`;
    const subjectBanner = `<div class="subj-banner">
        <div class="sb-head"><span class="sb-title">SUBJECT PROPERTY</span><span class="sb-addr">${esc(streetLine)}</span></div>
        <div class="sb-stats">
          ${bannerStat(isBuyer ? "List Price" : "Asking", subjPrice != null ? usd(subjPrice) : "—")}
          ${bannerStat("$/SqFt", subjPpsf != null ? usd(subjPpsf) : "—")}
          ${bannerStat("Beds", subjBeds != null ? String(subjBeds) : "—")}
          ${bannerStat("Baths", subjBaths != null ? String(subjBaths) : "—")}
          ${bannerStat("SqFt", subjSqft != null ? intStr(property.sqft) : "—")}
          ${bannerStat("Lot", subjLot != null ? intStr(property.lot) : "—")}
          ${bannerStat("Year", subjYear != null ? String(subjYear) : "—")}
        </div>
      </div>`;
    // dir: "g" = higher is more favorable (green); "r" = higher is less favorable (red); "" = neutral.
    const cell = (compVal, subjVal, dir, text) => {
      let cls = "";
      if (dir && compVal != null && subjVal != null && compVal !== subjVal) {
        const higher = compVal > subjVal;
        cls = dir === "g" ? (higher ? "fav" : "unfav") : (higher ? "unfav" : "fav");
      }
      return `<td class="num ${cls}">${text}</td>`;
    };
    const cmpRows = comps.map((c, i) => {
      const price = toNum(c.price), sq = toNum(c.sqft), bd = toNum(c.bedrooms), ba = toNum(c.bathrooms);
      const lot = toNum(c.lotSize), yr = toNum(c.yearBuilt), dom = toNum(c.daysOld);
      const ppsf = price != null && sq ? price / sq : null;
      return `<tr>
        <td class="num">${i + 1}</td>
        <td class="addr">${esc((c.address || "—").split(",")[0])}</td>
        ${cell(price, subjPrice, "r", price != null ? usd(price) : "—")}
        ${cell(ppsf, subjPpsf, "r", ppsf != null ? usd(ppsf) : "—")}
        ${cell(bd, subjBeds, "g", bd != null ? String(bd) : "—")}
        ${cell(ba, subjBaths, "g", ba != null ? String(ba) : "—")}
        ${cell(sq, subjSqft, "g", sq != null ? intStr(sq) : "—")}
        ${cell(lot, subjLot, "g", lot != null ? intStr(lot) : "—")}
        ${cell(yr, subjYear, "g", yr != null ? String(yr) : "—")}
        <td class="num">${dom != null ? Math.round(dom) : "—"}</td>
        <td class="num">${c.distance != null ? `${esc(String(c.distance))} mi` : "—"}</td>
      </tr>`;
    }).join("");
    const comparisonInner = `${header}
      <div class="pg-body">
        <h2 class="sec">Detailed Comparison</h2>
        ${subjectBanner}
        <table class="tbl mx">
          <thead><tr><th>#</th><th>Address</th><th class="num">Sale Price</th><th class="num">$/SqFt</th><th class="num">Bd</th><th class="num">Ba</th><th class="num">SqFt</th><th class="num">Lot</th><th class="num">Year</th><th class="num">DOM</th><th class="num">Dist</th></tr></thead>
          <tbody>${cmpRows}</tbody>
        </table>
        <p class="legend">Compared to the subject &mdash; <b class="lg-fav">green = more favorable</b> (more space/beds/baths, newer, or lower price); <b class="lg-unfav">red = less favorable</b> (less space, older, or higher price). Distance and days on market are shown for context.</p>
        <p class="foot-note">Comparables and value estimate via RentCast; street-level imagery via Google Street View. ${esc(today)}.</p>
      </div>`;

    // Assemble. Manual CMA = cover + overview only. Each page is one Letter sheet.
    // Pages 3-4 need comps to show; skip them when there are none (true manual CMA, or a
    // market-data CMA whose area returned no comparables). The overview note above explains which.
    const pages = noComps
      ? [{ cover: true, html: coverInner }, { html: overviewInner }]
      : [{ cover: true, html: coverInner }, { html: overviewInner }, { html: pricingInner }, { html: comparisonInner }];
    const total = pages.length;
    const sections = pages.map((pg, i) =>
      `<section class="page${pg.cover ? " cover" : ""}${i === total - 1 ? " last" : ""}">${pg.html}${footer(i + 1, total)}</section>`
    ).join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(docTitle)}${address ? ` — ${esc(streetLine)}` : ""}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap');
        @page { size: letter portrait; margin: 0; }
        :root { --accent: #1E3A5F; }
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .page { width: 8.5in; min-height: 11in; padding: 0.55in 0.6in 0.4in; display: flex; flex-direction: column; break-after: page; position: relative; }
        .page.last { break-after: auto; }
        .pg-body { flex: 1; }
        h2.sec { font-family: 'Fraunces', Georgia, serif; font-size: 14pt; font-weight: 600; color: #111; margin: 22px 0 12px; padding-bottom: 5px; border-bottom: 2px solid var(--accent); }
        .muted { color: #888; } .note { font-style: italic; font-size: 9.5pt; }
        .ph { background: #f1f2f4; border: 1px solid #e2e4e8; color: #9aa0a8; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 8pt; }

        .pg-header { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 8.5pt; color: #666; border-bottom: 1px solid #d9dce1; padding-bottom: 7px; }
        .ph-addr { flex: 1; } .ph-mid { flex: 1.4; text-align: center; color: #999; } .ph-right { flex: 1; text-align: right; font-weight: 600; color: #555; }
        .ph-logo { max-height: 20px; max-width: 90px; object-fit: contain; vertical-align: middle; }
        .pg-footer { display: flex; justify-content: space-between; gap: 10px; font-size: 7.5pt; color: #9aa0a8; border-top: 1px solid #e2e4e8; padding-top: 6px; margin-top: 14px; }

        /* Cover */
        .cover { text-align: center; }
        .cv-top { padding-top: 0.15in; }
        .cv-logo { max-height: 64px; max-width: 280px; object-fit: contain; }
        .cv-wordmark { font-family: 'Fraunces', Georgia, serif; font-size: 26pt; font-weight: 600; color: #111; }
        .cv-wordmark span { color: var(--accent); }
        .cv-kicker { font-size: 10.5pt; letter-spacing: 3px; color: #8a9099; margin-top: 8px; text-transform: uppercase; }
        .cv-hero { flex: 1; display: flex; align-items: center; justify-content: center; padding: 0.3in 0; }
        .cv-photo { width: 100%; aspect-ratio: 16 / 10; max-height: 4.2in; object-fit: cover; border-radius: 12px; box-shadow: 0 2px 14px rgba(0,0,0,0.16); }
        .cv-photo.ph { height: 3.6in; }
        .cv-addr-block { margin-bottom: 0.1in; }
        .cv-street { font-family: 'Fraunces', Georgia, serif; font-size: 24pt; font-weight: 600; color: #111; }
        .cv-locality { font-size: 12pt; color: #777; margin-top: 3px; }
        .cv-prep-divider { height: 2px; width: 64px; background: var(--accent); margin: 0.16in auto; }
        .cv-prep-row { display: flex; align-items: center; justify-content: center; gap: 14px; }
        .cv-headshot { width: 58px; height: 58px; border-radius: 50%; object-fit: cover; border: 2px solid var(--accent); }
        .cv-prep-txt { text-align: left; }
        .cv-prep-label { font-size: 8.5pt; text-transform: uppercase; letter-spacing: 1px; color: #999; }
        .cv-prep-name { font-size: 13pt; font-weight: 700; color: #111; }
        .cv-prep-co { font-size: 10.5pt; color: #666; }
        .cv-prep-date { font-size: 9pt; color: #999; margin-top: 2px; }

        /* Overview */
        .ov-top { display: flex; gap: 18px; margin-bottom: 4px; }
        .ov-imgs { flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .ov-photo { width: 100%; height: 2.5in; object-fit: cover; border-radius: 8px; }
        .ov-photo.ph { height: 2.5in; }
        .ov-map { width: 100%; height: 1.5in; object-fit: cover; border-radius: 8px; }
        .ov-map.ph { height: 1.5in; }
        .val-card { flex: 1; background: #f3f6fa; border: 1px solid #d6e0ec; border-radius: 10px; padding: 18px; display: flex; flex-direction: column; justify-content: center; }
        .val-label { font-size: 9pt; text-transform: uppercase; letter-spacing: 1px; color: #6b7785; }
        .val-figure { font-size: 32pt; font-weight: 700; color: var(--accent); line-height: 1.05; margin: 4px 0; }
        .val-range { font-size: 11pt; color: #444; }
        .val-rec { font-size: 10.5pt; color: #333; margin-top: 10px; }
        .val-note { font-size: 9.5pt; font-style: italic; color: #555; margin-top: 8px; }
        .spec-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .spec { border: 1px solid #e7e9ed; border-radius: 7px; padding: 9px 12px; }
        .spec-k { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; color: #8a9099; }
        .spec-v { font-size: 12pt; font-weight: 600; color: #111; margin-top: 2px; }
        .narr { font-size: 10.5pt; line-height: 1.5; margin: 0 0 9px; }

        /* Range bar */
        .rb { position: relative; margin: 30px 0 0; }
        .rb-track { height: 10px; border-radius: 5px; background: linear-gradient(90deg, #cdd9e8, var(--accent)); }
        .rb-mark { position: absolute; top: -4px; width: 18px; height: 18px; margin-left: -9px; border-radius: 50%; border: 3px solid var(--accent); background: #fff; }
        .rb-mark.ask { border-color: #c2410c; top: 14px; }
        .rb-mark span { position: absolute; bottom: 22px; left: 50%; transform: translateX(-50%); white-space: nowrap; font-size: 8pt; font-weight: 700; color: var(--accent); }
        .rb-mark.ask span { bottom: auto; top: 22px; color: #c2410c; }
        .rb-ticks { display: flex; justify-content: space-between; margin-top: 12px; font-size: 9pt; color: #66707d; }
        .rb-dot { position: absolute; top: 1px; width: 8px; height: 8px; margin-left: -4px; border-radius: 50%; background: #8a97a8; border: 1px solid #fff; box-sizing: border-box; }
        .rb-dot.oor { top: -1px; width: 12px; height: 12px; margin-left: -6px; background: #b91c1c; color: #fff; font-size: 8pt; font-weight: 700; line-height: 11px; text-align: center; }
        .rb-cap { margin-top: 10px; font-size: 8pt; font-style: italic; color: #66707d; }

        /* Tables */
        table.tbl { width: 100%; border-collapse: collapse; font-size: 9pt; margin-top: 4px; }
        table.tbl th { background: var(--accent); color: #fff; text-align: left; padding: 7px 8px; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 600; }
        table.tbl td { padding: 6px 8px; border-bottom: 1px solid #edeef1; vertical-align: middle; }
        table.tbl tbody tr:nth-child(even) { background: #f6f7f9; }
        .tbl .num { text-align: right; white-space: nowrap; }
        .tbl .addr { max-width: 1.5in; }
        .addr-badge { margin-top: 2px; }
        .cmp-thumb { width: 46px; height: 34px; object-fit: cover; border-radius: 3px; display: block; }
        td .cmp-thumb.ph { width: 46px; height: 34px; font-size: 6pt; }
        .badge { display: inline-block; padding: 1px 7px; border-radius: 9px; font-size: 7pt; font-weight: 700; }
        .b-sold { background: #e6f4ea; color: #137333; } .b-act { background: #e8f0fe; color: #1a56db; }
        .b-pend { background: #fef7e0; color: #b06000; } .b-comp { background: #eee; color: #666; }

        /* Comparison matrix coloring (override zebra) */
        table.tbl.mx th, table.tbl.mx td { padding: 6px 6px; }
        td.fav { background: #e6f4ea !important; color: #137333; font-weight: 600; }
        td.unfav { background: #fce8e6 !important; color: #c5221f; font-weight: 600; }

        /* Subject reference banner */
        .subj-banner { background: #f3f6fa; border: 1px solid #d6e0ec; border-left: 4px solid var(--accent); border-radius: 8px; padding: 12px 16px; margin-bottom: 12px; }
        .sb-head { display: flex; align-items: baseline; gap: 12px; margin-bottom: 8px; }
        .sb-title { font-size: 8pt; letter-spacing: 1.5px; color: var(--accent); font-weight: 700; }
        .sb-addr { font-size: 12pt; font-weight: 700; color: #111; }
        .sb-stats { display: flex; flex-wrap: wrap; gap: 20px; }
        .sb-stat { display: flex; flex-direction: column; }
        .sb-k { font-size: 7.5pt; text-transform: uppercase; color: #8a9099; }
        .sb-v { font-size: 11pt; font-weight: 700; color: #111; }

        .legend { font-size: 8pt; color: #66707d; margin-top: 10px; line-height: 1.5; }
        .lg-fav { color: #137333; } .lg-unfav { color: #c5221f; }
        .foot-note { font-size: 7.5pt; color: #9aa0a8; font-style: italic; margin-top: 8px; }
      </style></head><body>
      ${sections}
      <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 450); };</script>
      </body></html>`;

    if (openPrintDoc(html)) track("pdf_exported", { tool: "cma", mode: isBuyer ? "buyer" : "seller" });
  } catch (e) {
    toast.error("Could not open the report.");
  }
}
